import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { VehicleQuoteBlock } from "@/components/VehicleQuoteBlock";
import { Button } from "@/components/ui/button";
import { Download, Printer, ArrowLeft, Eye } from "lucide-react";
import { usePdfPreview } from "@/components/PdfPreviewDialog";

/*
 * Presentation view. Works two ways:
 *  - /proposals/:id/view  (authenticated agent preview)
 *  - /share/:token         (public read-only for dealership)
 */
export default function ProposalView({ shareMode = false }) {
  const params = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const fetch = async () => {
      try {
        if (shareMode) {
          const { data } = await api.get(`/share/${params.token}`);
          setP(data);
        } else {
          const { data } = await api.get(`/proposals/${params.id}`);
          setP(data);
        }
      } catch {
        setErr("Proposal not found");
      }
    };
    fetch();
  }, [params, shareMode]);

  const { preview, dialog: pdfDialog } = usePdfPreview();
  const previewPdf = () => {
    const path = shareMode
      ? `/share/${params.token}/pdf`
      : `/proposals/${params.id}/pdf`;
    preview({
      fetcher: () => api.get(path, { responseType: "blob" }),
      filename: `${(p?.title || "proposal").replace(/[^\w-]+/g, "_")}.pdf`,
      title: `Proposal · ${p?.title || ""}`,
    });
  };

  if (err) return <div className="min-h-screen grid place-items-center text-[#6C757D]">{err}</div>;
  if (!p) return <div className="min-h-screen grid place-items-center text-[#6C757D]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Toolbar (hidden in print) */}
      <div className="no-print sticky top-0 z-40 border-b border-[#E9ECEF] bg-white/85 backdrop-blur-xl">
        <div className="max-w-[1200px] mx-auto px-6 py-3 flex items-center justify-between">
          {!shareMode ? (
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#6C757D] hover:text-[#0B132B]">
              <ArrowLeft size={14} /> Dashboard
            </Link>
          ) : (
            <BrandLogo size="sm" />
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => window.print()}
              className="rounded-sm border-[#0B132B]/20"
              data-testid="print-button"
            >
              <Printer size={14} className="mr-2" /> Print
            </Button>
            <Button
              onClick={previewPdf}
              className="rounded-sm bg-[#D4AF37] hover:bg-[#c39d2e] text-[#0B132B] font-semibold"
              data-testid="pdf-download-button"
            >
              <Eye size={14} className="mr-2" /> Preview & Download
            </Button>
          </div>
        </div>
      </div>
      {pdfDialog}

      {/* Cover page */}
      <section className="print-page relative min-h-[calc(100vh-60px)] flex flex-col justify-center px-8 py-16 bg-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#D4AF37]/5 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#0B132B]/5 blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto text-center">
          <div className="flex justify-center mb-10">
            <BrandLogo size="xl" />
          </div>

          <div className="inline-block px-3 py-1 rounded-sm bg-[#0B132B] text-[#D4AF37] text-[10px] uppercase tracking-[0.32em] font-bold mb-8">
            Vehicle Service Contract Proposal
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black text-[#0B132B] tracking-tight leading-[1.02]">
            {p.title}
          </h1>

          {p.dealership && (
            <p className="mt-6 text-lg text-[#6C757D] font-medium">
              Prepared for <span className="text-[#0B132B] font-semibold">{p.dealership}</span>
            </p>
          )}

          <div className="mt-16 mx-auto w-16 h-[2px] bg-[#D4AF37]" />

          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold mb-2">
              Presented by
            </div>
            <div className="font-display text-2xl font-bold text-[#0B132B]">
              {p.agent_name}
            </div>
            {p.agency && (
              <div className="text-sm text-[#6C757D] mt-1">{p.agency}</div>
            )}
          </div>

          <div className="mt-16 text-xs uppercase tracking-[0.24em] text-[#6C757D] font-semibold">
            {new Date(p.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            {" • "}
            {p.vehicles?.length || 0} Vehicles
          </div>
        </div>
      </section>

      {/* Vehicle pages */}
      <section className="max-w-[1200px] mx-auto px-6 py-12 space-y-6">
        <div className="border-b border-[#E9ECEF] pb-4 mb-2">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Inventory Coverage
          </div>
          <h2 className="font-display text-3xl font-black text-[#0B132B] mt-1 tracking-tight">
            Vehicle-by-vehicle warranty options
          </h2>
        </div>
        {p.vehicles?.map((vq, i) => (
          <VehicleQuoteBlock
            key={i}
            vq={vq}
            index={i}
            markup={p.markup}
            categoryMarkups={p.category_markups}
            gapSettings={p.gap_settings}
            vscDealerType={p.vsc_dealer_type}
            selectedTerms={p.selected_terms}
            selectedProducts={p.selected_products}
          />
        ))}
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E9ECEF] mt-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-6">
          <BrandLogo size="sm" />
          <div className="text-xs text-[#6C757D] leading-relaxed max-w-md">
            All quotes are illustrative and subject to underwriting eligibility. Final
            contract terms, prices, and coverage are set by Advantage Warranty Group.
          </div>
        </div>
      </footer>
    </div>
  );
}
